/**
 * 쿼리 프로파일러 로그 파싱 유틸
 *
 * 백엔드(P6spyLogController → SqlLogBuffer)가 내려주는 로그 한 건의 형식:
 *   "/*[2026-09-04T10:11:12.345678]*\/\n <바인딩이 끝난 SQL 원문>"
 * p6spy 가 바인딩 값을 채운 SQL 만 남기므로 파라미터는 SQL 리터럴을 역으로 추출한다.
 */

export interface ProfilerLogRow {
  KeyField: string;
  no_seq: number;
  tm_log: string; // HH:mm:ss.SSS
  tm_full: string; // 서버 타임스탬프 원문
  tp_sql: string; // SELECT / INSERT / UPDATE / DELETE ...
  nm_table: string; // 추출된 테이블명 (콤마 구분)
  fg_sys: "Y" | "N" | "A"; // N 일반 / Y 시스템 / A 액세스(sys_access_log)
  dc_sql: string; // 그리드용 한 줄 미리보기
  txt_sql: string; // SQL 원문
  txt_raw: string; // 로그 원문 (중복 판정 키)
}

export interface ProfilerParamRow {
  KeyField: string;
  no_seq: number;
  nm_column: string;
  cd_oper: string;
  dc_value: string;
}

const LOG_PATTERN = /^\s*\/\*\[([^\]]*)\]\*\/\s*([\s\S]*)$/;
const PREVIEW_LENGTH = 300;

// FROM / JOIN 뒤에 오지만 테이블이 아닌 예약어
const NOT_TABLE_WORDS = new Set([
  "select",
  "set",
  "where",
  "values",
  "only",
  "lateral",
  "unnest",
  "generate_series",
  "dual",
  "if",
  "not",
  "exists",
]);

// 비교식 좌변으로 잡히면 안 되는 예약어
const NOT_COLUMN_WORDS = new Set([
  "and",
  "or",
  "not",
  "where",
  "on",
  "when",
  "then",
  "else",
  "case",
  "in",
  "is",
  "like",
  "set",
  "values",
  "select",
  "from",
  "join",
  "having",
  "limit",
  "offset",
]);

/** 로그 한 건 → 그리드 행 */
export function parseLogEntry(raw: string, seq: number): ProfilerLogRow {
  const m = LOG_PATTERN.exec(raw);
  const tm_full = m ? m[1] : "";
  const sql = (m ? m[2] : raw).trim();
  const stripped = stripComments(sql);
  const tables = extractTables(stripped);
  const tp_sql = detectType(stripped);

  return {
    KeyField: `p${seq}`,
    no_seq: seq,
    tm_log: formatTime(tm_full),
    tm_full,
    tp_sql,
    nm_table: tables.join(", "),
    fg_sys: classifyQuery(tables, tp_sql),
    dc_sql: toPreview(stripped),
    txt_sql: sql,
    txt_raw: raw,
  };
}

/** "2026-09-04T10:11:12.345678" → "10:11:12.345" */
export function formatTime(tm_full: string): string {
  if (!tm_full) return "";
  const t = tm_full.includes("T") ? tm_full.split("T")[1] : tm_full;
  return t.length > 12 ? t.slice(0, 12) : t;
}

/** 블록/라인 주석 제거 */
export function stripComments(sql: string): string {
  return sql
    .replace(/\/\*[\s\S]*?\*\//g, " ")
    .replace(/--[^\n]*/g, " ")
    .trim();
}

/** 첫 키워드로 SQL 구분 */
export function detectType(sql: string): string {
  const m = /^\s*\(*\s*([a-zA-Z]+)/.exec(sql);
  if (!m) return "ETC";
  const kw = m[1].toUpperCase();
  switch (kw) {
    case "SELECT":
    case "INSERT":
    case "UPDATE":
    case "DELETE":
    case "WITH":
    case "MERGE":
    case "CALL":
    case "CREATE":
    case "ALTER":
    case "DROP":
    case "TRUNCATE":
    case "BEGIN":
    case "COMMIT":
    case "ROLLBACK":
    case "SET":
    case "SHOW":
    case "EXPLAIN":
    case "DO":
      return kw;
    default:
      return "ETC";
  }
}

/** FROM / JOIN / INTO / UPDATE / TABLE 뒤의 식별자를 테이블로 간주 */
export function extractTables(sql: string): string[] {
  const found: string[] = [];

  // WITH 절에서 정의한 CTE 이름은 테이블이 아니다
  const ctes = new Set<string>();
  const cteRe =
    /(?:\bwith\s+(?:recursive\s+)?|,\s*)("?[a-zA-Z_][\w$]*"?)\s*(?:\([^)]*\)\s*)?as\s*\(/gi;
  let c: RegExpExecArray | null;
  while ((c = cteRe.exec(sql)) !== null) {
    ctes.add(c[1].replace(/"/g, "").toLowerCase());
  }

  const re =
    /\b(from|join|into|update|table)\s+(?!\()("?[a-zA-Z_][\w$]*"?(?:\."?[a-zA-Z_][\w$]*"?)?)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sql)) !== null) {
    const name = m[2].replace(/"/g, "");
    const bare = bareName(name).toLowerCase();
    if (NOT_TABLE_WORDS.has(bare)) continue;
    if (ctes.has(bare)) continue;
    // 스키마 접두 유무만 다른 같은 테이블은 한 번만
    if (!found.some((f) => bareName(f).toLowerCase() === bare)) {
      found.push(name);
    }
  }
  return found;
}

function bareName(table: string): string {
  return table.split(".").pop() || table;
}

/** SYS_ 접두 테이블 여부 (스키마 접두 허용) */
export function isSystemTable(table: string): boolean {
  return /^sys_/i.test(bareName(table));
}

/**
 * 시스템쿼리 분류 규칙: 접근 테이블이 전부 SYS_ 이면 시스템쿼리.
 * 업무 테이블과 SYS_ 를 함께 조인한 쿼리는 일반쿼리로 남겨 놓치지 않게 한다.
 * (분류 기준을 바꿀 때는 이 함수만 수정)
 */
export function isSystemQuery(tables: string[]): boolean {
  return tables.length > 0 && tables.every(isSystemTable);
}

function toPreview(sql: string): string {
  const oneLine = sql.replace(/\s+/g, " ").trim();
  return oneLine.length > PREVIEW_LENGTH
    ? oneLine.slice(0, PREVIEW_LENGTH) + " …"
    : oneLine;
}

/**
 * 문자열 리터럴을 자리표시자로 치환한다.
 * 리터럴 안의 = , ( ) 등이 파서를 오염시키지 않도록 하기 위함.
 */
function maskLiterals(sql: string): { masked: string; literals: string[] } {
  const literals: string[] = [];
  const masked = sql.replace(/'(?:[^']|'')*'/g, (lit) => {
    literals.push(lit);
    return `'§${literals.length - 1}§'`;
  });
  return { masked, literals };
}

function unmask(value: string, literals: string[]): string {
  return value.replace(/'§(\d+)§'/g, (_, i) => literals[Number(i)] ?? "");
}

/** 괄호를 고려해 최상위 콤마 기준으로 분리 (리터럴은 이미 마스킹된 상태) */
function splitTopLevel(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (cur.trim() !== "") out.push(cur.trim());
  return out;
}

/** start 위치의 '(' 에 대응하는 ')' 위치를 찾는다 (없으면 -1) */
function findClosingParen(text: string, start: number): number {
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    if (text[i] === "(") depth++;
    else if (text[i] === ")") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * 바인딩이 끝난 SQL 에서 컬럼-값 쌍을 추출한다.
 *  - INSERT ... (컬럼) VALUES (값) 은 위치 기준으로 짝을 맞춘다.
 *  - 그 외는 `컬럼 연산자 리터럴` 패턴(WHERE / SET / ON 절)을 찾는다.
 * 리터럴 기반 추정이므로 원본 바인드 파라미터와 완전히 같지는 않다.
 */
export function extractParams(sql: string): ProfilerParamRow[] {
  const stripped = stripComments(sql);
  const { masked, literals } = maskLiterals(stripped);
  const rows: ProfilerParamRow[] = [];
  const push = (nm_column: string, cd_oper: string, dc_value: string) => {
    rows.push({
      KeyField: `q${rows.length + 1}`,
      no_seq: rows.length + 1,
      nm_column,
      cd_oper,
      dc_value: unmask(dc_value, literals),
    });
  };

  // 1) INSERT INTO t (c1, c2) VALUES (v1, v2)
  const ins = /insert\s+into\s+"?[\w$]+"?(?:\."?[\w$]+"?)?\s*\(/i.exec(masked);
  if (ins) {
    const colOpen = ins.index + ins[0].length - 1;
    const colClose = findClosingParen(masked, colOpen);
    if (colClose > 0) {
      const afterCols = masked.slice(colClose + 1);
      const val = /^\s*values\s*\(/i.exec(afterCols);
      if (val) {
        const valOpen = colClose + 1 + val[0].length - 1;
        const valClose = findClosingParen(masked, valOpen);
        if (valClose > 0) {
          const cols = splitTopLevel(masked.slice(colOpen + 1, colClose));
          const vals = splitTopLevel(masked.slice(valOpen + 1, valClose));
          cols.forEach((col, i) =>
            push(col.replace(/"/g, ""), "=", vals[i] ?? "")
          );
        }
      }
    }
  }

  // 2) 컬럼 연산자 리터럴 (WHERE / SET / ON 절 등)
  const re =
    /("?[a-zA-Z_][\w$]*"?(?:\."?[a-zA-Z_][\w$]*"?)?)\s*(=|<>|!=|>=|<=|>|<|~~\*?|!~~\*?|\bnot\s+i?like\b|\bi?like\b|\bnot\s+in\b|\bin\b|\bis\s+not\b|\bis\b)\s*('§\d+§'|-?\d+(?:\.\d+)?\b|\bnull\b|\btrue\b|\bfalse\b|\((?:[^()]|\([^()]*\))*\))/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(masked)) !== null) {
    const column = m[1].replace(/"/g, "");
    if (NOT_COLUMN_WORDS.has(bareName(column).toLowerCase())) continue;
    const value = m[3];
    // 서브쿼리는 파라미터가 아니다
    if (value.startsWith("(") && /^\(\s*select\b/i.test(value)) continue;
    const oper = m[2].replace(/\s+/g, " ").toUpperCase();
    push(column, oper, value);
  }

  return rows;
}

/** sys_access_log 접근 여부 */
export function isAccessLogTable(table: string): boolean {
  return bareName(table).toLowerCase() === "sys_access_log";
}

/** sys_attachfile 접근 여부 */
export function isAttachFileTable(table: string): boolean {
  return bareName(table).toLowerCase() === "sys_attachfile";
}

/**
 * 탭 분류
 *  - sys_access_log 를 건드리면 액세스쿼리(A)
 *  - 전부 SYS_ 테이블이면 시스템쿼리(Y)
 *  - sys_attachfile 을 읽는 SELECT 는 업무 테이블과 조인돼 있어도 시스템쿼리(Y)
 *    (화면 진입마다 첨부파일 조회가 찍혀 일반쿼리 탭을 어지럽히므로)
 *  - 나머지 일반(N)
 * 액세스 로그 기록은 요청마다 찍혀 양이 많아 별도 탭으로 뺀다.
 */
export function classifyQuery(
  tables: string[],
  tp_sql: string = ""
): "Y" | "N" | "A" {
  if (tables.some(isAccessLogTable)) return "A";
  if (isSystemQuery(tables)) return "Y";
  if (
    (tp_sql === "SELECT" || tp_sql === "WITH") &&
    tables.some(isAttachFileTable)
  )
    return "Y";
  return "N";
}
